import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createColumnHelper } from '@tanstack/react-table';
import {
  fetchReminderLogs,
  deleteReminderLog,
  selectAllReminderLogs,
  selectReminderLogsInitialFetchDone,
  setFilters,
  selectReminderLogsFilters,
  selectReminderLogsLoading,
} from 'store/slices/reminderLogsSlice';
import { selectSettings } from 'store/slices/authSlice';
import Page from 'components/layout/Page/Page';
import { Table, StatusCell } from 'components/tables/';
import { formatDateToLocal, getDateTimePatterns } from 'utils/date';
import {
  REMINDER_TYPES_LABELS,
  REMINDER_TYPES_OPTIONS,
} from 'constants/reminders';
import { REMINDER_LOG_STATUSES_FILTER_OPTIONS } from 'constants/reminderLogs';
import DeleteModal from 'components/layout/modals/DeleteModal/DeleteModal';
import { FilterBar, useFilterBar } from 'components/filter';
import ReminderTabs from './components/ReminderTabs/ReminderTabs';

const columnHelper = createColumnHelper();

const REMINDER_LOG_TYPE_FILTER_OPTIONS = [
  { value: 'ALL', label: 'All' },
  ...REMINDER_TYPES_OPTIONS,
];

const REMINDER_LOG_VISIBLE_FILTERS = [
  {
    key: 'search',
    type: 'search',
    label: 'Search',
    placeholder: 'Search by reminder subject',
    tier: 'primary',
  },
  {
    key: 'status',
    type: 'select',
    label: 'Status',
    options: REMINDER_LOG_STATUSES_FILTER_OPTIONS,
    tier: 'primary',
  },
  {
    key: 'type',
    type: 'select',
    label: 'Type',
    options: REMINDER_LOG_TYPE_FILTER_OPTIONS,
    tier: 'primary',
  },
];

const REMINDER_LOG_FILTER_CONFIG = {
  route: '/reminders/logs',
  filters: {
    reminderId: {
      type: 'search',
      urlKey: 'reminderId',
      default: '',
      preserveReduxFallback: true,
    },
    search: {
      type: 'search',
      urlKey: 'search',
      default: '',
    },
    status: {
      type: 'select',
      urlKey: 'status',
      default: 'ALL',
      options: REMINDER_LOG_STATUSES_FILTER_OPTIONS,
    },
    type: {
      type: 'select',
      urlKey: 'type',
      default: 'ALL',
      options: REMINDER_LOG_TYPE_FILTER_OPTIONS,
    },
  },
  fetchThunk: fetchReminderLogs,
  setFilters,
  selectFilters: selectReminderLogsFilters,
  selectLoading: selectReminderLogsLoading,
  selectInitialFetchDone: selectReminderLogsInitialFetchDone,
};


function ReminderLogs() {
  const dispatch = useDispatch();

  const items = useSelector(selectAllReminderLogs);
  const loading = useSelector(selectReminderLogsLoading);
  const settings = useSelector(selectSettings);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);
  const {
    filterValues,
    isInitialLoading,
    hasActiveFilters,
    applyFilters,
    resetFilters,
    refetchCurrentPage,
  } = useFilterBar(REMINDER_LOG_FILTER_CONFIG);
  const { dateTimePattern: executedAtFormat } = getDateTimePatterns(settings);

  const handleDeleteClick = useCallback((log) => {
    setLogToDelete(log);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteModalOpen(false);
    setLogToDelete(null);
  }, []);

  const handleConfirmDelete = async () => {
    if (logToDelete) {
      try {
        await dispatch(deleteReminderLog(logToDelete._id)).unwrap();
        handleDeleteClose();
        refetchCurrentPage();
      } catch (_error) {
        // error handled by redux
      }
    }
  };
  const columns = useMemo(() => [
    columnHelper.accessor('reminder.subject', {
      header: 'Name',
      cell: (info) => info.row.original.reminder?.subject || 'N/A',
    }),
    columnHelper.accessor('reminder.type', {
      header: 'Type',
      cell: (info) => info.row.original.reminder?.type ? REMINDER_TYPES_LABELS[info.row.original.reminder.type] : 'N/A',
    }),
    columnHelper.accessor('executedAt', {
      header: 'Executed At',
      cell: (info) => formatDateToLocal(info.getValue(), executedAtFormat),
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => <StatusCell status={info.getValue()} />,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <button
          type="button"
          onClick={() => handleDeleteClick(info.row.original)}
          className="font-semibold text-red-400 hover:text-red-300 cursor-pointer"
        >
          Delete<span className="sr-only">, log {info.row.original._id}</span>
        </button>
      ),
    }),
  ], [executedAtFormat, handleDeleteClick]);

  return (
    <Page title="Reminder Logs" headerActions={<ReminderTabs />}>
      <Table
        data={items}
        columns={columns}
        loading={isInitialLoading}
        leftAction={
          <FilterBar
            filters={REMINDER_LOG_VISIBLE_FILTERS}
            values={filterValues}
            onChange={applyFilters}
            onReset={resetFilters}
            hasActiveFilters={hasActiveFilters}
            loading={isInitialLoading}
          />
        }
      />
      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteClose}
        title="Delete Log"
        message={`Are you sure you want to delete this log? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        loading={loading}
      />
    </Page>
  );
}

export default ReminderLogs;
